<template lang="pug">
div
  h3 CMP Metadata
  div( v-if="loading" ) Loading...
  div( v-else )
    div( class="alert alert-danger exists-alert" v-if="isUnderReview" ) This advertiser is already under review for having a problem CMP
    div( class="alert alert-warning exists-alert" v-if="cmpMetadata.id" ) CMP metadata already entered
    div( class="checkbox-container" )

      p
        label( class="form-check-label" )
          input( class="form-check-input" type="checkbox" v-model="cmpMetadata.hasCmp" v-bind:disabled="saving" )
          | Has CMP

      h4 Buttons / Links
      p
        label( class="form-check-label" )
          input( class="form-check-input" type="checkbox" v-model="cmpMetadata.hasAcceptAll" v-bind:disabled="inputDisabled" )
          | Accept All
      p
        label( class="form-check-label" )
          input( class="form-check-input" type="checkbox" v-model="cmpMetadata.hasManage" v-bind:disabled="inputDisabled" )
          | Manage
      p
        label( class="form-check-label" )
          input( class="form-check-input" type="checkbox" v-model="cmpMetadata.hasDecline" v-bind:disabled="inputDisabled" )
          | Decline/Essential only
      p
        label( class="form-check-label" )
          input( class="form-check-input" type="checkbox" v-model="cmpMetadata.hasOther" v-bind:disabled="inputDisabled" )
          | Other
      
      h4 Additional Info
      p
        label( class="form-check-label" )
          input( class="form-check-input" type="checkbox" v-model="cmpMetadata.hasComplex" v-bind:disabled="inputDisabled" )
          | Complex (e.g. 2-step / scroll / &gt;1 interaction)

      p
        label( class="form-check-label" )
          input( class="form-check-input" type="checkbox" v-model="cmpMetadata.isForced" v-bind:disabled="inputDisabled" )
          | Forced Engagement?
          br
          a( v-bind:href="auditLink" v-if="auditLink" )
            | Check
            i( class="fa fa-external-link ml-2" )
      
      p( class="pull-back" )
        label Size
        select( class="form-control" v-model="cmpMetadata.size" v-bind:disabled="inputDisabled" )
          option( v-bind:value="null" ) Please select size
          option( value="small" ) Small
          option( value="typical" ) Typical
          option( value="big" ) Big
      
      p( class="pull-back" )
        label Placement
        select( class="form-control" v-model="cmpMetadata.placement" v-bind:disabled="inputDisabled" )
          option( v-bind:value="null" ) Please select placement
          option( value="top" ) Top
          option( value="middle" ) Middle
          option( value="bottom" ) Bottom
          option( value="left" ) Left
          option( value="right" ) Right
      
      p( class="pull-back" )
        label Conclusion
        select( class="form-control" v-model="cmpMetadata.conclusion" v-bind:disabled="inputDisabled" )
          option( v-bind:value="null" ) Please select a conclusion
          option( value="Cookiebar - not a CMP" ) Cookiebar - not a CMP
          option( value="No CMP" ) No CMP
          option( value="CMP - three button" ) CMP - three button
          option( value="CMP - complex" ) CMP - complex
          option( value="CMP - Moonpull interacted incorrectly" ) CMP - Moonpull interacted incorrectly
          option( value="CMP - Not displayed to Lite" ) CMP - Not displayed to Lite
          option( value="CMP - Not displayed to Classic" ) CMP - Not displayed to Classic
          option( value="CMP - beyond Moonpull" ) CMP - Beyond Moonpull
          option( value="CMP - other" ) CMP - other
      
      p( class="pull-back" )
        label( class="form-check-label" ) Comment
          textarea( class="form-control w-100" rows="5" placeholder="Comments" v-model="cmpMetadata.comment" v-bind:disabled="saving" )
      p
        button( class="btn btn-success mt-4" v-on:click="saveCmpMetadata" v-bind:disabled="saving" )
          span( v-if="saving" ) Saving...
          span( v-else ) Save

</template>

<style lang="scss" scoped>
.checkbox-container {
  margin-left: 20px;

  h4 {
    margin-left: -20px;
  }
}
.pull-back {
  margin-left: -20px;
}
.exists-alert {
  word-break: break-word;
}
</style>

<script>
import Vue from 'vue'
import { ref, onBeforeMount, computed } from '@vue/composition-api'
import { api } from '@/api'
import { flashMessage } from '@/store'
export default Vue.extend({
  props: [ 'auditId', 'auditLink', 'batchCmpMetadata', 'isUnderReview' ],
  setup(props) {

    const moonpullSession = localStorage.getItem('moonpullSession')
    const { user: { id: currentUserId } } = JSON.parse(moonpullSession)

    const loading = ref(true)
    const cmpMetadata = ref(null)
    const saving = ref(false)
    const inputDisabled = computed(() => ! cmpMetadata.value.hasCmp || saving.value)

    onBeforeMount(async () => {
      const existingMetadata = props.batchCmpMetadata?.find(m => m.auditId === props.auditId)
      // Found existing metadata
      if (existingMetadata) {
        cmpMetadata.value = { ...existingMetadata }
      }
      // Initialise cold state if we can't find the metadata
      if (! existingMetadata) {
        cmpMetadata.value = {
          auditId: props.auditId,
          hasCmp: false,
          hasAcceptAll: false,
          hasManage: false,
          hasDecline: false,
          hasComplex: false,
          hasOther: false,
          isForced: false,
          size: null,
          placement: null,
          conclusion: null,
          comment: null,
          createdById: currentUserId,
        }
      }
      loading.value = false
    })

    async function createNewCmpMetadata() {
      const response = await api('POST', '/cmps/metadata', {
        auditId: cmpMetadata.value.auditId,
        hasCmp: cmpMetadata.value.hasCmp,
        hasAcceptAll: cmpMetadata.value.hasAcceptAll,
        hasManage: cmpMetadata.value.hasManage,
        hasDecline: cmpMetadata.value.hasDecline,
        hasComplex: cmpMetadata.value.hasComplex,
        hasOther: cmpMetadata.value.hasOther,
        isForced: cmpMetadata.value.isForced,
        size: cmpMetadata.value.size,
        placement: cmpMetadata.value.placement,
        conclusion: cmpMetadata.value.conclusion,
        comment: cmpMetadata.value.comment,
        createdById: currentUserId,
      })
      if (! response.ok) {
        flashMessage('danger', await response.text())
        return
      }
      flashMessage('success', 'CMP metadata saved')
      cmpMetadata.value = await response.json()
    }

    async function updateExistingCmpMetadata() {
      const response = await api('PATCH', `/cmps/metadata/${cmpMetadata.value.id}`, {
        hasCmp: cmpMetadata.value.hasCmp,
        hasAcceptAll: cmpMetadata.value.hasAcceptAll,
        hasManage: cmpMetadata.value.hasManage,
        hasDecline: cmpMetadata.value.hasDecline,
        hasComplex: cmpMetadata.value.hasComplex,
        hasOther: cmpMetadata.value.hasOther,
        isForced: cmpMetadata.value.isForced,
        size: cmpMetadata.value.size,
        placement: cmpMetadata.value.placement,
        conclusion: cmpMetadata.value.conclusion,
        comment: cmpMetadata.value.comment,
      })
      if (! response.ok) {
        flashMessage('danger', await response.text())
        return
      }
      flashMessage('success', 'CMP metadata saved')
      cmpMetadata.value = await response.json()
    }

    async function saveCmpMetadata() {
      saving.value = true
      if (cmpMetadata.value.id) {
        await updateExistingCmpMetadata()
      }
      else {
        await createNewCmpMetadata()
      }
      saving.value = false
    }

    return {
      loading,
      cmpMetadata,
      inputDisabled,
      saveCmpMetadata,
      saving,
    }
  },
})
</script>
